// 🔥 모델 카드 컴포넌트 - 개별 모델 요약 및 확장 가능한 가격 테이블
// [배경] 기기 모델 목록에서 모델 단위로 그룹핑하여 표시
//        용량/연결성은 Chip으로 요약, 가격은 확장 시 테이블로 표시
//        태블릿/스마트폰/웨어러블 공통 사용

import React, { useState, memo, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  Chip,
  Avatar,
  Checkbox
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Settings as SettingsIcon,
  AddCircleOutline as AddStorageIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  Tablet as TabletIcon,
  Headphones as HeadphonesIcon
} from '@mui/icons-material';

import VariantChipGroup, { VariantInfo } from './VariantChipGroup';
import { DeviceCatalogDocument } from '../../../../types/deviceCatalog';
import { GradePriceInfo, DeviceGrade } from '../../../../types/gradeSystem';

// 📌 가격 행 데이터 인터페이스
export interface PriceRowData {
  priceId: string;
  storage: string;
  connectivity: string;
  prices: GradePriceInfo;
  hasPrice: boolean;
}

// 📌 Props 인터페이스
export interface ModelCardProps {
  /** 모델 정보 */
  model: DeviceCatalogDocument;
  /** 해당 모델의 모든 가격 행 */
  priceRows: PriceRowData[];
  /** 디바이스 타입 */
  deviceType: 'tablet' | 'smartphone' | 'wearable';
  /** 브랜드 */
  brand: 'Apple' | 'Samsung';
  /** 선택 여부 */
  isSelected?: boolean;
  /** 선택 변경 핸들러 */
  onSelectionChange?: (modelId: string, selected: boolean) => void;
  /** 모델 설정 클릭 핸들러 */
  onSettingsClick?: (model: DeviceCatalogDocument) => void;
  /** 용량 관리 클릭 핸들러 */
  onVariantManageClick?: (model: DeviceCatalogDocument) => void;
  /** 모델 삭제 클릭 핸들러 */
  onDeleteClick?: (model: DeviceCatalogDocument) => void;
  /** 확장 상태 (외부 제어용) */
  expanded?: boolean;
  /** 확장 변경 핸들러 */
  onExpandChange?: (modelId: string, expanded: boolean) => void;
  /** 가격 테이블 렌더러 (외부에서 인라인 편집 테이블 전달) */
  renderPriceTable?: (priceRows: PriceRowData[]) => React.ReactNode;
  /** 행 인덱스 (배경색 번갈아가며) */
  index?: number;
  /** 🔥 변경된 셀 개수 (리렌더링 트리거용) */
  changedCellCount?: number;
  /** 🔥 저장된 셀 개수 (리렌더링 트리거용) */
  savedCellCount?: number;
}

// 🎧 등급 명칭 반환 함수
const getGradeLabel = (grade: DeviceGrade): string => {
  return `${grade}등급`;
};

/**
 * 🔥 모델 카드 컴포넌트
 * 
 * @description
 * 개별 디바이스 모델을 카드 형태로 표시합니다.
 * - 모델명, 시리즈, 용량 옵션을 요약 표시
 * - 확장 시 가격 테이블 표시
 * - 모델 설정 / 용량 관리 / 삭제 액션 분리
 */
const ModelCard: React.FC<ModelCardProps> = ({
  model,
  priceRows,
  deviceType,
  brand,
  isSelected = false,
  onSelectionChange,
  onSettingsClick,
  onVariantManageClick,
  onDeleteClick,
  expanded: externalExpanded,
  onExpandChange,
  renderPriceTable,
  index = 0,
  changedCellCount = 0,
  savedCellCount = 0
}) => {
  // 📊 내부 확장 상태 (외부 제어가 없을 때 사용)
  const [internalExpanded, setInternalExpanded] = useState(false);
  
  // 📊 실제 확장 상태 (외부 제어 우선)
  const isExpanded = externalExpanded !== undefined ? externalExpanded : internalExpanded;

  // 📊 통계 계산
  const stats = useMemo(() => {
    const totalVariants = priceRows.length;
    const withPrice = priceRows.filter(r => r.hasPrice && Object.values(r.prices).some(p => p > 0)).length;
    const uniqueStorages = new Set(priceRows.map(r => r.storage)).size;
    
    return {
      totalVariants,
      withPrice,
      withoutPrice: totalVariants - withPrice,
      uniqueStorages,
      priceSetRate: totalVariants > 0 ? Math.round((withPrice / totalVariants) * 100) : 0
    };
  }, [priceRows]);

  // 📊 Variant 정보 생성 (VariantChipGroup용)
  const variantInfos: VariantInfo[] = useMemo(() => {
    return priceRows.map(row => ({
      storage: row.storage,
      connectivity: row.connectivity,
      hasPrice: row.hasPrice && Object.values(row.prices).some(p => p > 0),
      priceA: row.prices.A
    }));
  }, [priceRows]);

  // 🔄 확장 토글 핸들러
  const handleExpandToggle = useCallback(() => {
    const newExpanded = !isExpanded;
    if (onExpandChange) {
      onExpandChange(model.id, newExpanded);
    } else {
      setInternalExpanded(newExpanded);
    }
  }, [isExpanded, model.id, onExpandChange]);

  // 🔄 선택 변경 핸들러
  const handleSelectionChange = useCallback(() => {
    onSelectionChange?.(model.id, !isSelected);
  }, [model.id, isSelected, onSelectionChange]);

  // 🎨 디바이스 아이콘 반환
  const getDeviceIcon = () => {
    switch (deviceType) {
      case 'tablet':
        return <TabletIcon />;
      case 'smartphone':
        return <PhoneIcon />;
      case 'wearable':
        return <HeadphonesIcon />;
      default:
        return <PhoneIcon />;
    }
  };

  // 🎨 가격 상태 색상
  const getPriceStatusColor = () => {
    if (stats.priceSetRate === 100) return '#10b981';
    if (stats.priceSetRate > 0) return '#f59e0b';
    return '#ef4444';
  };

  // 🖱️ 더블클릭 핸들러 (펼치기/접기)
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    // 버튼, 체크박스, input 등은 제외
    if ((e.target as HTMLElement).closest('button, input, .MuiIconButton-root, .MuiCheckbox-root')) {
      return;
    }
    handleExpandToggle();
  }, [handleExpandToggle]);

  return (
    <Card
      elevation={isExpanded ? 3 : 1}
      onDoubleClick={handleDoubleClick}
      sx={{
        mb: 1,
        borderRadius: 2,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: index % 2 === 0 ? 'background.paper' : 'grey.50',
        transition: 'all 0.2s ease',
        cursor: 'default',
        userSelect: 'none',
        '&:hover': {
          boxShadow: 3,
          borderColor: isSelected ? 'primary.main' : 'primary.light'
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* 🔝 메인 행: 체크박스 + 모델 정보 + 용량 Chip + 액션 버튼 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* 체크박스 */}
          {onSelectionChange && (
            <Checkbox
              checked={isSelected}
              onChange={handleSelectionChange}
              size="small"
              sx={{ p: 0.5 }}
            />
          )}

          {/* 모델 아이콘/이미지 */}
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: brand === 'Apple' ? '#374151' : '#1e40af',
              fontSize: '1.2rem'
            }}
          >
            {getDeviceIcon()}
          </Avatar>

          {/* 모델 정보 */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {model.modelKo}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                {model.modelEn}
              </Typography>
              <Chip
                label={model.series}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
              {/* 가격 설정률 표시 */}
              <Chip
                icon={stats.priceSetRate === 100 ? <CheckIcon sx={{ fontSize: 12 }} /> : 
                      stats.priceSetRate > 0 ? undefined : <WarningIcon sx={{ fontSize: 12 }} />}
                label={`${stats.withPrice}/${stats.totalVariants} 설정`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: getPriceStatusColor(),
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            </Box>
          </Box>

          {/* 용량 Chip 그룹 */}
          <Box sx={{ flex: '0 0 auto', maxWidth: 300 }}>
            <VariantChipGroup
              variants={variantInfos}
              deviceType={deviceType}
              maxDisplay={5}
              compact
            />
          </Box>

          {/* 액션 버튼들 */}
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            {/* 모델 설정 */}
            <Tooltip title="모델 정보 수정 (시리즈, 이미지 등)">
              <IconButton
                size="small"
                onClick={() => onSettingsClick?.(model)}
                sx={{
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' }
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* 용량/구성 관리 */}
            {(
              <Tooltip title="용량/구성 관리 (새 옵션 추가)">
                <IconButton
                  size="small"
                  onClick={() => onVariantManageClick?.(model)}
                  color="success"
                  sx={{
                    bgcolor: 'success.50',
                    '&:hover': { bgcolor: 'success.100' }
                  }}
                >
                  <AddStorageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* 삭제 */}
            <Tooltip title="모델 삭제">
              <IconButton
                size="small"
                onClick={() => onDeleteClick?.(model)}
                color="error"
                sx={{
                  bgcolor: 'error.50',
                  '&:hover': { bgcolor: 'error.100' }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* 확장/축소 */}
            <Tooltip title={isExpanded ? '가격 테이블 접기' : '가격 테이블 펼치기'}>
              <IconButton
                size="small"
                onClick={handleExpandToggle}
                sx={{
                  bgcolor: isExpanded ? 'primary.main' : 'grey.100',
                  color: isExpanded ? 'white' : 'inherit',
                  '&:hover': {
                    bgcolor: isExpanded ? 'primary.dark' : 'grey.200'
                  }
                }}
              >
                {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* 📊 확장 영역: 가격 테이블 */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px dashed',
              borderColor: 'divider'
            }}
          >
            {renderPriceTable ? (
              // 외부에서 전달된 가격 테이블 렌더러 사용
              renderPriceTable(priceRows)
            ) : (
              // 기본 가격 테이블 (인라인 편집 없이)
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem' }}>
                        용량
                      </th>
                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem' }}>
                        연결성
                      </th>
                      {(['A', 'B', 'C', 'D'] as DeviceGrade[]).map(grade => (
                        <th
                          key={grade}
                          style={{
                            padding: '8px',
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        >
                          {getGradeLabel(grade)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {priceRows.map((row, idx) => (
                      <tr
                        key={row.priceId}
                        style={{
                          backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc'
                        }}
                      >
                        <td style={{ padding: '8px', fontWeight: 600, fontSize: '0.8rem' }}>
                          {row.storage}
                        </td>
                        <td style={{ padding: '8px', fontSize: '0.8rem', color: '#6b7280' }}>
                          {deviceType === 'smartphone' && row.connectivity === '기본' 
                            ? 'Cellular' 
                            : row.connectivity}
                        </td>
                        {(['A', 'B', 'C', 'D'] as DeviceGrade[]).map(grade => (
                          <td
                            key={grade}
                            style={{
                              padding: '8px',
                              textAlign: 'center',
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              color: row.prices[grade] > 0 ? '#374151' : '#94a3b8'
                            }}
                          >
                            {row.prices[grade] > 0
                              ? `${row.prices[grade].toLocaleString()}원`
                              : '미설정'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// 🔧 displayName 설정
ModelCard.displayName = 'ModelCard';

// 🔥 React.memo로 최적화
export default memo(ModelCard, (prevProps, nextProps) => {
  return (
    prevProps.model.id === nextProps.model.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.expanded === nextProps.expanded &&
    prevProps.priceRows.length === nextProps.priceRows.length &&
    prevProps.deviceType === nextProps.deviceType &&
    // 🔥 변경/저장 셀 개수 비교 (중요! 인라인 편집 반영)
    prevProps.changedCellCount === nextProps.changedCellCount &&
    prevProps.savedCellCount === nextProps.savedCellCount &&
    // 가격 변경 감지 (간단한 비교)
    JSON.stringify(prevProps.priceRows.map(r => r.prices)) === 
    JSON.stringify(nextProps.priceRows.map(r => r.prices))
  );
});
